import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Button } from '@progress/kendo-react-buttons';
import { Loader } from '@progress/kendo-react-indicators';
import { CreatingField } from '../../../components/CreatingField';
import { fetchApi } from '../../../services/api';
import { JsonResponse } from 'TypeGen/json-response';
import { WorkOrderTaskDetailViewModel, WorkOrderTaskPartViewModel, formatCurrency, formatStatusLabel } from './WorkOrderDetail';
import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';

interface Props {
  task: WorkOrderTaskDetailViewModel;
  onClose: () => void;
  onCompleted: () => void;
}

interface FormState {
  actualHours: number | null;
  note: string;
}

interface PartLocationOption {
  PartLocationID: number;
  LocationName: string;
  QuantityOnHand: number;
}

const buildPartsTotal = (parts: WorkOrderTaskPartViewModel[]) => parts.reduce((total, part) => total + (part.QuantityUsed * part.CostAtTime), 0);

const CompleteWorkOrderTaskDialog: React.FC<Props> = ({ task, onClose, onCompleted }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>({
    actualHours: task.ActualHours ?? task.EstimatedHours ?? null,
    note: ''
  });
  const [locationOptionsByPartId, setLocationOptionsByPartId] = useState<Record<number, PartLocationOption[]>>({});
  const [selectedLocations, setSelectedLocations] = useState<Record<number, number | null>>({});
  const [loadingLocations, setLoadingLocations] = useState(false);

  const isTechnicianAssigned = useMemo(() => Boolean(task.AssignedTo), [task.AssignedTo]);

  useEffect(() => {
    const initialSelections: Record<number, number | null> = {};
    task.Parts.forEach((part) => {
      initialSelections[part.WorkOrderTaskPartID] = part.PartLocationID ?? null;
    });
    setSelectedLocations(initialSelections);
  }, [task.WorkOrderTaskID]);

  const fetchLocationsForPart = useCallback(async (partId: number) => {
    const response = await fetchApi(`/api/ShopInventory/PartDetail?PartID=${partId}`) as {
      Locations?: PartLocationOption[];
    };

    return (response?.Locations ?? []).map((location) => ({
      PartLocationID: location.PartLocationID,
      LocationName: location.LocationName,
      QuantityOnHand: location.QuantityOnHand,
    }));
  }, []);

  useEffect(() => {
    const loadLocations = async () => {
      const inventoryParts = task.Parts.filter((part): part is WorkOrderTaskPartViewModel & { PartID: number } => !part.IsCustomPart && typeof part.PartID === 'number');

      if (!inventoryParts.length) {
        setLocationOptionsByPartId({});
        setLoadingLocations(false);
        return;
      }

      setLoadingLocations(true);
      try {
        const uniquePartIds = Array.from(new Set(inventoryParts.map((part) => part.PartID as number)));
        const locationEntries = await Promise.all(uniquePartIds.map(async (partId) => {
          try {
            const locations = await fetchLocationsForPart(partId);
            return [partId, locations] as const;
          } catch (innerError) {
            console.error(`Failed to load locations for part ${partId}.`, innerError);
            return [partId, [] as PartLocationOption[]] as const;
          }
        }));

        const optionsMap: Record<number, PartLocationOption[]> = {};
        locationEntries.forEach(([partId, options]) => {
          optionsMap[partId] = options;
        });
        setLocationOptionsByPartId(optionsMap);

        setSelectedLocations((prev) => {
          const next = { ...prev };
          inventoryParts.forEach((part) => {
            if (next[part.WorkOrderTaskPartID] == null) {
              const options = optionsMap[part.PartID] ?? [];
              if (options.length > 0) {
                const defaultOption = options.reduce((a, b) => a.QuantityOnHand >= b.QuantityOnHand ? a : b, options[0]);
                next[part.WorkOrderTaskPartID] = defaultOption.PartLocationID;
              }
            }
          });
          return next;
        });
      } catch (err) {
        console.error('Failed to load part locations for completion.', err);
        setError('Unable to load inventory locations. Try again before completing the task.');
      } finally {
        setLoadingLocations(false);
      }
    };

    loadLocations();
  }, [fetchLocationsForPart, task.WorkOrderTaskID]);

  const partsTotal = useMemo(() => buildPartsTotal(task.Parts), [task.Parts]);
  const hasPartsWithoutUsage = useMemo(() => task.Parts.some((part) => part.QuantityUsed <= 0), [task.Parts]);
  const isLocationSelectionMissing = task.Parts.some((part) => {
    if (part.IsCustomPart || part.QuantityUsed <= 0) {
      return false;
    }
    const partId = typeof part.PartID === 'number' ? part.PartID : null;
    if (partId == null) {
      return false;
    }
    const options = locationOptionsByPartId[partId] ?? [];
    if (!options.length) {
      return false;
    }
    const selected = selectedLocations[part.WorkOrderTaskPartID];
    return !selected;
  });

  const handleSave = async () => {
    if (saving || loadingLocations) {
      return;
    }

    if (!isTechnicianAssigned) {
      setError('Assign a technician before completing the task.');
      return;
    }

    const partsMissingLocation = task.Parts.filter((part) => {
      if (part.IsCustomPart || part.QuantityUsed <= 0) {
        return false;
      }
      const partId = typeof part.PartID === 'number' ? part.PartID : null;
      if (partId == null) {
        return false;
      }
      const options = locationOptionsByPartId[partId] ?? [];
      if (!options.length) {
        return false;
      }
      return !selectedLocations[part.WorkOrderTaskPartID];
    });

    if (partsMissingLocation.length > 0) {
      setError('Select a location for each part before completing the task.');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      for (const part of task.Parts) {
        if (part.IsCustomPart) {
          continue;
        }

        const partId = typeof part.PartID === 'number' ? part.PartID : null;
        if (partId == null) {
          continue;
        }
        const selectedLocationId = selectedLocations[part.WorkOrderTaskPartID] ?? null;
        const originalLocationId = part.PartLocationID ?? null;

        if (!selectedLocationId || selectedLocationId === originalLocationId) {
          continue;
        }

        const updatePayload = {
          WorkOrderTaskPartID: part.WorkOrderTaskPartID,
          QuantityUsed: part.QuantityUsed,
          CostAtTime: part.CostAtTime,
          Note: part.Note ?? null,
          PartLocationID: selectedLocationId
        };

        const updateResponse = await fetchApi('/api/ShopWorkOrder/UpdateWorkOrderTaskPart', updatePayload, 'POST') as JsonResponse;
        if (!updateResponse?.Success) {
          throw new Error(updateResponse?.ErrorMessage || 'Unable to update part location.');
        }
      }

      const payload = {
        WorkOrderTaskID: task.WorkOrderTaskID,
        ActualHours: form.actualHours ?? null,
        Note: form.note.trim().length ? form.note.trim() : null
      };

      const response = await fetchApi('/api/ShopWorkOrder/CompleteWorkOrderTask', payload, 'POST') as JsonResponse;
      if (!response?.Success) {
        throw new Error(response?.ErrorMessage || 'Unable to complete task.');
      }

      onCompleted();
    } catch (err: any) {
      console.error('Failed to complete work order task.', err);
      setError(err?.message || 'Unable to complete task.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog title="Complete Work Order Task" onClose={onClose} className="dialog-w800">
      <div className="k-form k-form-md">
        {saving && (
          <div className="mb-3 d-flex justify-content-center">
            <Loader type="converging-spinner" />
          </div>
        )}

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        {!isTechnicianAssigned && !error && (
          <div className="alert alert-warning" role="alert">
            Assign a technician before completing the task.
          </div>
        )}

        <div className="mb-3">
          <div className="fw-bold">{task.Description}</div>
          <div className="text-muted small">
            Assigned to {task.AssignedToName || 'Unassigned'} · Current status {formatStatusLabel(task.Status)}
          </div>
        </div>

        <div className="alert alert-info" role="alert">
          <h4 className="alert-heading">Inventory Reminder</h4>
          Completing this task will deduct the parts listed below from inventory and update the work order totals. If quantities need adjusting, update them before completing the task.
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Parts Used</label>
          {task.Parts.length === 0 ? (
            <p className="text-muted mb-0">No parts recorded for this task.</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm mb-0">
                <thead>
                  <tr>
                    <th>Part</th>
                    <th>Location</th>
                    <th className="text-end">Quantity</th>
                    <th className="text-end">Cost</th>
                    <th className="text-end">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {task.Parts.map((part) => {
                    const partId = typeof part.PartID === 'number' ? part.PartID : null;
                    const options = partId != null ? (locationOptionsByPartId[partId] ?? []) : [];
                    const dropdownOptions = options.map((option) => ({
                      ...option,
                      label: option.LocationName,
                    }));
                    const selectedLocationId = selectedLocations[part.WorkOrderTaskPartID] ?? null;
                    const selectedOption = dropdownOptions.find((option) => option.PartLocationID === selectedLocationId) ?? null;
                    const showSelectionWarning = !part.IsCustomPart && part.QuantityUsed > 0 && options.length > 0 && !selectedLocationId && !loadingLocations;
                    const defaultItem = {
                      PartLocationID: 0,
                      LocationName: 'Select location',
                      QuantityOnHand: 0,
                      label: 'Select location'
                    };

                    return (
                      <tr key={part.WorkOrderTaskPartID}>
                        <td>
                          <div className="fw-bold">
                            {part.IsCustomPart ? (part.CustomPartDescription || part.PartDescription || 'Custom Part') : part.PartNumber}
                          </div>
                          <div className="text-muted small">
                            {part.IsCustomPart ? 'Non-inventory part' : part.PartDescription}
                          </div>
                        </td>
                        <td style={{ minWidth: '14rem' }}>
                          {part.IsCustomPart ? (
                            <span className="text-muted small">Not required</span>
                          ) : loadingLocations && !options.length ? (
                            <div className="d-flex align-items-center">
                              <Loader type="infinite-spinner" />
                              <span className="ms-2 small">Loading locations...</span>
                            </div>
                          ) : options.length > 0 ? (
                            <>
                              <DropDownList
                                data={dropdownOptions}
                                textField="label"
                                dataItemKey="PartLocationID"
                                value={selectedOption ?? null}
                                defaultItem={defaultItem}
                                onChange={(event: DropDownListChangeEvent) => {
                                  const value = event.value as typeof dropdownOptions[number] | typeof defaultItem | null;
                                  setSelectedLocations((prev) => ({
                                    ...prev,
                                    [part.WorkOrderTaskPartID]: value && 'PartLocationID' in value && value.PartLocationID !== 0 ? value.PartLocationID : null
                                  }));
                                  setError(null);
                                }}
                                disabled={saving}
                                style={{ width: '100%' }}
                              />
                              <div className="text-muted small mt-1">
                                {selectedLocationId
                                  ? `On hand: ${options.find((option) => option.PartLocationID === selectedLocationId)?.QuantityOnHand ?? 0}`
                                  : `Available across locations: ${options.reduce((sum, option) => sum + option.QuantityOnHand, 0)}`}
                              </div>
                              {showSelectionWarning && (
                                <div className="text-warning small mt-1">Select a location for this part.</div>
                              )}
                            </>
                          ) : (
                            <div className={part.LocationName?.length ? 'text-muted small' : 'text-warning small'}>
                              {part.LocationName?.length ? part.LocationName : 'No locations configured'}
                            </div>
                          )}
                        </td>
                        <td className="text-end">{part.QuantityUsed}</td>
                        <td className="text-end">{formatCurrency(part.CostAtTime)}</td>
                        <td className="text-end">{formatCurrency(part.QuantityUsed * part.CostAtTime)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={4} className="text-end fw-bold">Parts Total</td>
                    <td className="text-end fw-bold">{formatCurrency(partsTotal)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
          {hasPartsWithoutUsage && (
            <div className="text-warning small mt-2">
              One or more parts have a quantity of 0. Inventory will not be adjusted for those items.
            </div>
          )}
          {isLocationSelectionMissing && !loadingLocations && (
            <div className="text-warning small mt-2">
              Select a location for each part that will be consumed before completing the task.
            </div>
          )}
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Actual Hours</label>
          <CreatingField
            dataType="number"
            data={form.actualHours}
            inputProps={{ format: 'n1', min: 0, step: 0.5, placeholder: 'Optional', disabled: saving }}
            onChange={(value) => setForm((prev) => ({ ...prev, actualHours: value }))}
          />
          <small className="text-muted">Leave blank if actual hours are the same as estimated.</small>
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Completion Notes</label>
          <CreatingField
            data={form.note}
            renderEditable={(value, onChange) => (
              <textarea
                className="form-control"
                rows={3}
                maxLength={2048}
                disabled={saving}
                value={value}
                onChange={(e) => onChange(e.target.value)}
              />
            )}
            onChange={(value) => setForm((prev) => ({ ...prev, note: value }))}
          />
        </div>

      </div>
      <DialogActionsBar>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          themeColor="primary"
          type="button"
          disabled={saving || loadingLocations || isLocationSelectionMissing || !isTechnicianAssigned}
          onClick={handleSave}
        >
          {saving ? 'Completing...' : 'Complete Task'}
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
};

export default CompleteWorkOrderTaskDialog;
